import { createContext, useCallback, useContext, useMemo } from 'react'
import type { JSX, ReactNode } from 'react'

import type { Service } from '../../graphqlEnums'
import type { TakeawayChannel } from './types'

/**
 * Shares what the whole section knows — which page it serves, the channels its
 * takeaways resolve against, and what the CTAs may open — so the card does not
 * carry props it never reads on their way to a grandchild.
 *
 * Only values that hold for every card belong here. Anything that varies per
 * card, or changes as the user interacts, stays a prop.
 */
interface TakeawaysContextValue {
  variant: 'single-channel' | 'cross-channel'
  /** Channels a takeaway's `channelIds` resolve against. */
  channelsById: Map<string, TakeawayChannel>
  channelService?: Service | null
  canCreatePosts: boolean
  onOpenRecommendedTimesDialog?: (channelId: string) => void
}

const TakeawaysContext = createContext<TakeawaysContextValue | null>(null)

interface TakeawaysProviderProps {
  variant: 'single-channel' | 'cross-channel'
  channels?: ReadonlyArray<TakeawayChannel>
  channelService?: Service | null
  canCreatePosts: boolean
  onOpenRecommendedTimesDialog?: (channelId: string) => void
  children: ReactNode
}

export function TakeawaysProvider({
  variant,
  channels,
  channelService,
  canCreatePosts,
  onOpenRecommendedTimesDialog,
  children,
}: TakeawaysProviderProps): JSX.Element {
  const value = useMemo<TakeawaysContextValue>(
    () => ({
      variant,
      channelsById: new Map(
        (channels ?? []).map((channel) => [channel.id, channel]),
      ),
      channelService,
      canCreatePosts,
      onOpenRecommendedTimesDialog,
    }),
    [
      variant,
      channels,
      channelService,
      canCreatePosts,
      onOpenRecommendedTimesDialog,
    ],
  )

  return (
    <TakeawaysContext.Provider value={value}>
      {children}
    </TakeawaysContext.Provider>
  )
}

function useTakeawaysContext(): TakeawaysContextValue {
  const value = useContext(TakeawaysContext)
  if (value == null) {
    throw new Error(
      'Takeaway components must be used within <Takeaways>. Render the section rather than its parts.',
    )
  }
  return value
}

/** Which badge the page wants on every card. */
export function useTakeawayVariant(): TakeawaysContextValue['variant'] {
  return useTakeawaysContext().variant
}

/** The service whose copy and composer prefill the CTAs use by default. */
export function useTakeawayChannelService(): Service | null | undefined {
  return useTakeawaysContext().channelService
}

/**
 * Resolves a takeaway's `channelIds` to channels, dropping any the page did not
 * supply. Callers never touch the lookup itself.
 */
export function useTakeawayChannels(
  channelIds: ReadonlyArray<string> | null | undefined,
): TakeawayChannel[] {
  const { channelsById } = useTakeawaysContext()
  return useMemo(
    () =>
      (channelIds ?? []).flatMap((id) => {
        const channel = channelsById.get(id)
        return channel ? [channel] : []
      }),
    [channelIds, channelsById],
  )
}

/**
 * What the CTAs are allowed to open. Composing is asked per channel, because a
 * cross-channel takeaway acts on its first channel rather than on the section:
 * an actor may be able to post to one channel a card names but not another.
 */
export function useTakeawayCtaOptions(): {
  canCreatePostsOn: (channelId: string) => boolean
  onOpenRecommendedTimesDialog?: (channelId: string) => void
} {
  const { canCreatePosts, channelsById, onOpenRecommendedTimesDialog } =
    useTakeawaysContext()
  const canCreatePostsOn = useCallback(
    (channelId: string): boolean =>
      // A page that supplies no per-channel answer leaves the section-wide flag
      // to decide, which is what a single-channel page wants.
      canCreatePosts && (channelsById.get(channelId)?.canCreatePosts ?? true),
    [canCreatePosts, channelsById],
  )
  return { canCreatePostsOn, onOpenRecommendedTimesDialog }
}
